/**
 * Entities → what a bubble draws (web-plan.md F1): the web twin of the
 * desktop's `entities.rs` blocks/Run pass. Offsets are UTF-16 code units
 * (D14) — which is exactly `String.prototype.slice`'s unit, the reason this
 * file has no conversion layer at all (the desktop needed one; JS is the
 * platform the offsets were designed for).
 *
 * Unknown entity types style nothing (chat-api.md §7): the text under them
 * renders bare, and the bubble carries one "some formatting is not
 * supported" line — `hasUnknownEntities` is that flag.
 */
import type { Entity } from "../gen/dto";
import { ENTITY_TYPES } from "../gen/dto";

export type InlineStyle =
  | { t: "bold" }
  | { t: "italic" }
  | { t: "underline" }
  | { t: "strike" }
  | { t: "spoiler" }
  | { t: "code" }
  | { t: "text_link"; url: string }
  | { t: "url" }
  | { t: "mention"; userId: number };

export interface Run {
  text: string;
  styles: InlineStyle[];
}

export type Block =
  | { t: "para"; runs: Run[] }
  | { t: "pre"; text: string; language: string | null }
  | { t: "quote"; runs: Run[]; expandable: boolean };

/** The link target of a run: an explicit text_link wins over an autolinked
 * url (the former was typed on purpose). */
export function runLink(run: Run): string | null {
  for (const s of run.styles) {
    if (s.t === "text_link") return s.url;
  }
  return run.styles.some((s) => s.t === "url") ? run.text : null;
}

export function runMention(run: Run): number | null {
  for (const s of run.styles) {
    if (s.t === "mention") return s.userId;
  }
  return null;
}

const KNOWN: ReadonlySet<string> = new Set(ENTITY_TYPES);

export function hasUnknownEntities(entities: Entity[]): boolean {
  return entities.some((e) => !KNOWN.has((e as { type: string }).type));
}

interface Span {
  offset: number;
  end: number;
  style: InlineStyle;
}

/** The block pass: `pre` and `blockquote` carve the text into top-level
 * segments; everything between them is a paragraph of styled runs. The
 * input is the server's normal form — pre/blockquote never overlap anything
 * except full nesting of code inside a quote, which renders as the quote's
 * inline code. */
export function blocksOf(text: string, entities: Entity[]): Block[] {
  const known = entities.filter((e) => KNOWN.has((e as { type: string }).type));
  const blocks: Block[] = [];
  type BlockSpan = { offset: number; end: number; e: Entity };
  const blockSpans: BlockSpan[] = known
    .filter((e) => {
      const t = (e as { type: string }).type;
      return t === "pre" || t === "blockquote";
    })
    .map((e) => ({ offset: e.offset, end: e.offset + e.length, e }));
  // Full nesting inside a blockquote is legal; the OUTER block wins the
  // segmentation and the inner code renders as inline style.
  const top = blockSpans.filter(
    (b) =>
      !blockSpans.some(
        (o) => o !== b && o.offset <= b.offset && b.end <= o.end && o.end - o.offset > b.end - b.offset,
      ),
  );
  top.sort((a, b) => a.offset - b.offset);

  let cursor = 0;
  for (const b of top) {
    if (b.offset > cursor) pushPara(blocks, text, known, cursor, b.offset);
    const type = (b.e as { type: string }).type;
    if (type === "pre") {
      const language = (b.e as { language?: string | null }).language ?? null;
      blocks.push({ t: "pre", text: text.slice(b.offset, b.end), language });
    } else {
      blocks.push({
        t: "quote",
        runs: runsOf(text, known, b.offset, b.end),
        expandable: Boolean((b.e as { expandable?: boolean }).expandable),
      });
    }
    cursor = b.end;
  }
  if (cursor < text.length) pushPara(blocks, text, known, cursor, text.length);
  if (blocks.length === 0 && text.length > 0) blocks.push({ t: "para", runs: [{ text, styles: [] }] });
  return blocks;
}

function pushPara(blocks: Block[], text: string, entities: Entity[], from: number, to: number): void {
  const slice = text.slice(from, to);
  if (slice === "") return;
  blocks.push({ t: "para", runs: runsOf(text, entities, from, to) });
}

/** The run pass over [from, to): boundaries at every entity edge; each run
 * carries every inline style covering it. */
export function runsOf(text: string, entities: Entity[], from: number, to: number): Run[] {
  const spans: Span[] = [];
  for (const e of entities) {
    const style = inlineStyleOf(e);
    if (!style) continue;
    const start = Math.max(e.offset, from);
    const end = Math.min(e.offset + e.length, to);
    if (start < end) spans.push({ offset: start, end, style });
  }
  const edges = new Set<number>([from, to]);
  for (const s of spans) {
    edges.add(s.offset);
    edges.add(s.end);
  }
  const sorted = [...edges].filter((x) => x >= from && x <= to).sort((a, b) => a - b);
  const runs: Run[] = [];
  for (let i = 0; i + 1 < sorted.length; i += 1) {
    const a = sorted[i]!;
    const b = sorted[i + 1]!;
    if (a === b) continue;
    const slice = text.slice(a, b);
    if (slice === "") continue;
    const styles = spans.filter((s) => s.offset <= a && b <= s.end).map((s) => s.style);
    runs.push({ text: slice, styles });
  }
  return runs;
}

function inlineStyleOf(e: Entity): InlineStyle | null {
  const t = (e as { type: string }).type;
  switch (t) {
    case "bold":
      return { t: "bold" };
    case "italic":
      return { t: "italic" };
    case "underline":
      return { t: "underline" };
    case "strike":
      return { t: "strike" };
    case "spoiler":
      return { t: "spoiler" };
    case "code":
      return { t: "code" };
    case "text_link":
      return { t: "text_link", url: (e as { url: string }).url };
    case "url":
      return { t: "url" };
    case "mention":
      return { t: "mention", userId: (e as { user_id: number }).user_id };
    default:
      return null; // pre/blockquote are block-level; unknown styles nothing
  }
}
